import { useMemo, useSyncExternalStore } from 'react';
import { TASK_STATUSES, TASK_STATUS_META, type TaskDto, type TaskStatus } from '../../api/tasks';
import { dueState } from '../ProjectPage/my-task-due';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import './task-viz.css';

/**
 * task-viz (2026-08-26) — 태스크(프로젝트) 화면의 **작은 시각화 조각들**. 신규 파일.
 *
 * 이 화면은 숫자만 있고 그림이 없었다: 칸반 컬럼은 건수만, 하단 도크는 문장만 말했다.
 * 그래서 "리뷰에 쌓였다" · "지연이 어느 칸에 몰렸다" 같은 것을 카드를 세어야 알았다.
 *
 * 여기 있는 것들은 전부 **줄을 늘리지 않는** 크기로 만든다 — 칸반은 카드를 보는 화면이라
 * 그림이 카드 자리를 뺏으면 지고 들어가는 거래다. 컬럼 미터는 헤더 **테두리 위에 겹쳐**
 * 그리고(높이 0), 도크 미니바는 이미 있는 한 줄 안에 들어간다.
 *
 * ⚠ 프로젝트 제목 아래 진행률 %·바는 **일부러 만들지 않는다** — 2026-06-11 에 사용자가
 *   빼 달라고 한 자리다(TaskPage.tsx 주석). 같은 정보를 같은 자리에 되살리지 않는다.
 *
 * 색은 화면의 기존 규칙을 그대로 쓴다: TASK_STATUS_META 의 상태색 + 지연=빨강.
 */

/** 지연 = 미완료 + 마감일이 지남. 내 업무 화면과 같은 판정(dueState)을 쓴다. */
export function isOverdue(t: Pick<TaskDto, 'dueDate' | 'status'>): boolean {
  return dueState(t.dueDate, t.status) === 'overdue';
}

/**
 * 칸반 컬럼 미터 — 컬럼 헤더의 아래 테두리에 겹쳐 그리는 2px 막대.
 *
 * 길이 = 이 컬럼 건수 / 가장 큰 컬럼 건수. 즉 "어느 칸이 부었나" 를 컬럼을 세지 않고 본다.
 * 그 안에서 **지연분은 빨강**이라, 같은 길이라도 상한 칸과 밀린 칸이 구분된다.
 * 높이를 차지하지 않으므로(absolute) 카드가 보이는 영역은 종전 그대로다.
 */
export function ColumnMeter({ status, count, overdue, max }: {
  status: TaskStatus;
  count: number;
  overdue: number;
  max: number;
}) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  if (count === 0) return null;

  const meta = TASK_STATUS_META[status];
  const width = max > 0 ? Math.max((count / max) * 100, 6) : 0;
  // 지연 폭은 **컬럼 막대 안에서의 비율** — 전체 대비로 재면 막대를 넘친다.
  const overWidth = count > 0 ? (overdue / count) * 100 : 0;

  return (
    <span
      className="tvz-meter"
      style={{ width: `${width}%` }}
      role="img"
      aria-label={tTask('task.kanban.meter_aria', {
        label: meta.label, n: String(count), over: String(overdue),
      })}
    >
      <span className="tvz-meter-fill" style={{ background: meta.color }} />
      {overdue > 0 && <span className="tvz-meter-over" style={{ width: `${overWidth}%` }} />}
    </span>
  );
}

/**
 * 상태 분포 미니 스택 바 — 하단 진행 현황 도크의 한 줄 안에 들어가는 그림.
 *
 * 도크 바는 "팀원 N명 · 진행중 N건" 문장뿐이라, 이 프로젝트가 시작 단계인지 마무리
 * 단계인지가 안 보였다. 스택 한 줄이면 그게 보인다. 완료율(%)을 옆에 적어 그림이
 * 말하는 값을 숫자로도 준다 — 그림만 아는 정보를 만들지 않는다.
 */
export function StatusMiniBar({ tasks }: { tasks: TaskDto[] }) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);

  const { slices, total, pct } = useMemo(() => {
    const c: Record<TaskStatus, number> = { TODO: 0, IN_PROGRESS: 0, REVIEW: 0, DONE: 0 };
    for (const t of (Array.isArray(tasks) ? tasks : [])) {
      if (t.status in c) c[t.status as TaskStatus] += 1;
    }
    const sum = TASK_STATUSES.reduce((a, s) => a + c[s], 0);
    return {
      slices: TASK_STATUSES.map((s) => ({ status: s, n: c[s] })).filter((x) => x.n > 0),
      total: sum,
      pct: sum > 0 ? Math.round((c.DONE / sum) * 100) : 0,
    };
  }, [tasks]);

  if (total === 0) return null;

  return (
    <span className="tvz-mini">
      <span
        className="tvz-minibar"
        role="img"
        aria-label={slices.map((s) => `${TASK_STATUS_META[s.status].label} ${s.n}`).join(', ')}
      >
        {slices.map((s) => (
          <span
            key={s.status}
            className="tvz-miniseg"
            style={{ flexGrow: s.n, background: TASK_STATUS_META[s.status].color }}
            title={`${TASK_STATUS_META[s.status].label} ${s.n}`}
          />
        ))}
      </span>
      <b className="tvz-minipct">{tTask('task.dock.done_pct', { n: String(pct) })}</b>
    </span>
  );
}
